import type { OrganizationTaskStatusEnum } from './tasks';

export const ProposalSubType = {
  BALANCE_DUE: 'BALANCE_DUE',
  UNFILED_RETURN: 'UNFILED_RETURN',
} as const;

export const ProposalStatus = {
  DRAFT: 'DRAFT',
  AWAITING_CUSTOMER: 'AWAITING_CUSTOMER',
  APPROVED: 'APPROVED',
  DECLINED: 'DECLINED',
} as const;

export const ProposalSource = {
  TAX_OPS: 'TAX_OPS',
  CUSTOMER_REQUEST: 'CUSTOMER_REQUEST',
} as const;

export type ProposalSubType = (typeof ProposalSubType)[keyof typeof ProposalSubType];
export type ProposalStatus = (typeof ProposalStatus)[keyof typeof ProposalStatus];
export type ProposalSource = (typeof ProposalSource)[keyof typeof ProposalSource];

export interface ProposalItem {
  id: string;
  proposal_id: string;
  state_code: string;
  period_year: number | null;
  period_month: number | null;
  amount: string | null;
  linked_filing_id: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface Proposal {
  id: string;
  organization_id: string;
  created_by: string;
  assignee: string | null;
  sub_type: ProposalSubType;
  status: ProposalStatus;
  source: ProposalSource;
  detail: string;
  decline_reason: string | null;
  items: ProposalItem[];
  created_at: string;
  updated_at: string | null;
}

/**
 * Where a proposal is in its life. The backend works it out from the status: an accepted proposal is
 * `COMPLETED` once every balance due task is complete, or every back filing exists. Until then it is `ACCEPTED`.
 */
export const ProposalStage = {
  DRAFT: 'DRAFT',
  AWAITING_CUSTOMER: 'AWAITING_CUSTOMER',
  ACCEPTED: 'ACCEPTED',
  COMPLETED: 'COMPLETED',
  DECLINED: 'DECLINED',
} as const;

export type ProposalStage = (typeof ProposalStage)[keyof typeof ProposalStage];

export const ProposalOrder = {
  NEWEST: 'NEWEST',
  DAYS_WAITING: 'DAYS_WAITING',
} as const;

export type ProposalOrder = (typeof ProposalOrder)[keyof typeof ProposalOrder];

export interface AdminProposalItemTask {
  id: string;
  status: OrganizationTaskStatusEnum;
}

export interface AdminProposalItem extends ProposalItem {
  balance_due_task: AdminProposalItemTask | null;
  /** True when this accepted balance-due item has no payment task. */
  missing_payment_task: boolean;
}

export interface AdminProposal extends Omit<Proposal, 'items'> {
  items: AdminProposalItem[];
  organization_name: string | null;
  created_by_name: string;
  stage: ProposalStage;
  total_amount: string | null;
  /** UTC, with no offset. */
  sent_at: string | null;
  /** UTC, with no offset. */
  decided_at: string | null;
  decided_by: string | null;
  decided_by_name: string | null;
}

export interface AdminProposalStageCounts {
  draft: number;
  awaiting_customer: number;
  accepted: number;
  completed: number;
  declined: number;
}

export interface AdminProposalPage {
  items: AdminProposal[];
  total: number;
  page: number;
  size: number;
  pages: number;
  /** Counts for every stage. They apply every filter except `stage`, so each tab shows its own total. */
  stage_counts: AdminProposalStageCounts;
}

export interface CrossOrgProposalListParams {
  stage?: ProposalStage;
  sub_type?: ProposalSubType;
  state_code?: string;
  /** Inclusive UTC day, `YYYY-MM-DD`. */
  sent_from?: string;
  /** Inclusive UTC day, `YYYY-MM-DD`. */
  sent_to?: string;
  created_by?: string;
  organization_search?: string;
  /** When true, only accepted balance-due proposals that have an item with no payment task. */
  missing_payment_task?: boolean;
  order_by?: ProposalOrder;
  page?: number;
  size?: number;
}

export interface ProposalItemCreatePayload {
  state_code: string;
  period_year: number | null;
  period_month: number | null;
  amount: string | null;
}

export interface ProposalCreatePayload {
  sub_type: ProposalSubType;
  source: ProposalSource;
  detail: string;
  assignee: string | null;
  items: ProposalItemCreatePayload[];
}

export interface ProposalBulkCreateRequest {
  proposals: ProposalCreatePayload[];
}

export interface ProposalItemPatchPayload {
  id: string;
  period_year: number | null;
  period_month: number | null;
  amount: string | null;
}

export interface ProposalPatchRequest {
  detail: string;
  assignee: string | null;
  items: ProposalItemPatchPayload[];
}

export interface ProposalBulkSendRequest {
  proposal_ids: string[];
}

/** A state notice Tax Ops attached to a balance-due proposal for the customer to read. */
export interface ProposalNotice {
  id: string;
  file_name: string;
  mime_type: string;
  file_size_bytes: number;
  created_at: string;
}
